import React from 'react';
import { Search, X, EyeOff, Eye } from 'react-feather';

/**
 * ConfigToolbar — the first row inside an `it-frame`; identical layout on
 * every Configuration page:
 *
 *   [search] [All n | Unmapped n | Mapped n] [extra chips…]  ……  [Show deleted] [meta] [actions]
 *
 * props
 *   search       { value, onChange(str), placeholder }
 *   status       { value: 'all'|'unmapped'|'mapped', counts: { all, unmapped, mapped }, onChange(v),
 *                  labels?: { all, unmapped, mapped } }
 *   chips        node — extra filter chips (use <FilterChip>)
 *   showDeleted  { value: bool, onChange(bool), count? }
 *   meta         node — right-side read-only info (mono)
 *   actions      node — buttons (use <ToolbarButton>); at most one `primary`
 *   children     node — rendered after search (escape hatch)
 */
export default function ConfigToolbar({ search, status, chips, showDeleted, meta, actions, children }) {
  return (
    <div className="cp-toolbar">
      {search ? (
        <div className="cp-search">
          <Search size={14} />
          <input
            type="text"
            value={search.value || ''}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder || 'Search…'}
            aria-label={search.placeholder || 'Search'}
          />
          {search.value ? (
            <button type="button" className="cp-search-clear" onClick={() => search.onChange('')} aria-label="Clear search">
              <X size={12} />
            </button>
          ) : null}
        </div>
      ) : null}
      {status ? <StatusSegment {...status} /> : null}
      {chips}
      {children}
      <div className="cp-toolbar-spacer" />
      {showDeleted ? (
        <button
          type="button"
          className={`cp-btn cp-ghost${showDeleted.value ? ' cp-active' : ''}`}
          onClick={() => showDeleted.onChange(!showDeleted.value)}
          aria-pressed={showDeleted.value}
        >
          {showDeleted.value ? <Eye size={14} /> : <EyeOff size={14} />}
          {showDeleted.value ? 'Showing deleted' : 'Show deleted'}
          {showDeleted.count != null ? <span className="cp-n cp-meta">{showDeleted.count}</span> : null}
        </button>
      ) : null}
      {meta ? <div className="cp-meta">{meta}</div> : null}
      {actions}
    </div>
  );
}

/** All / Unmapped / Mapped segmented control. */
export function StatusSegment({ value = 'all', counts = {}, onChange, labels = {} }) {
  const items = [
    { key: 'all', label: labels.all || 'All', n: counts.all },
    { key: 'unmapped', label: labels.unmapped || 'Unmapped', n: counts.unmapped, warn: (counts.unmapped || 0) > 0 },
    { key: 'mapped', label: labels.mapped || 'Mapped', n: counts.mapped },
  ];
  return (
    <div className="cp-seg" role="group" aria-label="Mapping status filter">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={`${value === it.key ? 'cp-active' : ''}${it.warn ? ' cp-warn' : ''}`}
          onClick={() => onChange && onChange(it.key)}
          aria-pressed={value === it.key}
        >
          {it.label}
          {it.n != null ? <span className="cp-n">{it.n}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** A toggle filter chip with an optional count. */
export function FilterChip({ active, warn, count, onClick, children, title }) {
  return (
    <button
      type="button"
      className={`cp-chip${active ? ' cp-active' : ''}${warn ? ' cp-warn' : ''}`}
      onClick={onClick}
      aria-pressed={!!active}
      title={title}
    >
      {children}
      {count != null ? <span className="cp-n">{count}</span> : null}
    </button>
  );
}

/**
 * ToolbarButton — variants: 'default' | 'primary' | 'ai' | 'danger' | 'ghost'.
 * `icon` is a react-feather component.
 */
export function ToolbarButton({ variant = 'default', icon: Icon, children, active, small, iconOnly, className, ...rest }) {
  const cls = [
    'cp-btn',
    variant === 'primary' && 'cp-primary',
    variant === 'ai' && 'cp-ai',
    variant === 'danger' && 'cp-danger',
    variant === 'ghost' && 'cp-ghost',
    active && 'cp-active',
    small && 'cp-sm',
    iconOnly && 'cp-icon',
    // Merged, not spread — `...rest` is applied after className below, so an
    // incoming className would otherwise replace every variant class.
    className,
  ].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} {...rest}>
      {Icon ? <Icon size={14} /> : null}
      {iconOnly ? null : children}
    </button>
  );
}
